"use client";

import { Input } from "@/components/ui/Input";
import { useLanguage } from "@/context/LanguageContext";
import { FormDataMyEstate, FormDataWatchDog } from "@/types";
import React, { useEffect, useRef, useState } from "react";
import cities from "../../utils/cities.json";

interface CitySelectProps<T extends FormDataWatchDog | FormDataMyEstate> {
  formData: T;
  setFormData: React.Dispatch<React.SetStateAction<T>>;
  isNew: boolean;
}

export default function CitySelect<
  T extends FormDataWatchDog | FormDataMyEstate,
>({ formData, setFormData, isNew }: CitySelectProps<T>) {
  const { language } = useLanguage();
  const copy = {
    cs: {
      cityPlaceholder: "Zadejte město",
      cityNotFound: "Město nenalezeno",
    },
    en: {
      cityPlaceholder: "Enter a city",
      cityNotFound: "City not found",
    },
  }[language];
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState(formData.exactAddress || "");
  const [filtered, setFiltered] = useState<string[]>([]);
  const [hasTyped, setHasTyped] = useState(false);
  const [selectedCity, setSelectedCity] = useState(query);

  // Deduplicate cities
  const uniqueCities = Array.from(new Set(cities));

  // Normalize accents for search
  const removeAccents = (str: string) =>
    str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

  const updateExactAddress = (exactAddress: string) => {
    setFormData((prev) => {
      const nextData = {
        ...prev,
        exactAddress,
      } as T & {
        doResetSearch?: boolean;
        doResetWatchDog?: boolean;
      };

      if ("doResetSearch" in prev) {
        nextData.doResetSearch = !isNew;
      }

      if ("doResetWatchDog" in prev) {
        nextData.doResetWatchDog = !isNew;
      }

      return nextData as T;
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHasTyped(true);
    const value = e.target.value;
    setQuery(value);

    const normalizedValue = removeAccents(value.toLowerCase());

    const exactMatch = uniqueCities.find(
      (c) => removeAccents(c.toLowerCase()) === normalizedValue,
    );

    // ✅ If exact match, save immediately
    if (exactMatch) {
      setSelectedCity(exactMatch);
      updateExactAddress(exactMatch);
      setFiltered([]);
      return;
    }

    // Otherwise filter suggestions
    const matches = uniqueCities
      .filter((c) => removeAccents(c.toLowerCase()).includes(normalizedValue))
      .sort((a, b) => {
        const aStarts = removeAccents(a.toLowerCase()).startsWith(
          normalizedValue,
        )
          ? 0
          : 1;
        const bStarts = removeAccents(b.toLowerCase()).startsWith(
          normalizedValue,
        )
          ? 0
          : 1;
        return aStarts - bStarts;
      })
      .slice(0, 5);

    setFiltered(matches);
  };
  const noMatch = hasTyped && filtered.length === 0 && query.trim() !== "";

  const handleSelect = (city: string) => {
    setQuery(city);
    setSelectedCity(city);
    setFiltered([]);
    updateExactAddress(city);
  };

  const handleBlur = () => {
    if (uniqueCities.includes(query) && query !== selectedCity) {
      setSelectedCity(query);
      updateExactAddress(query);
    }
    setQuery(selectedCity || "");
    setFiltered([]);
  };

  const showDropdown =
    (filtered.length > 0 || noMatch) && !uniqueCities.includes(query);

  // Hide dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setFiltered([]);
        handleBlur();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [query, selectedCity]);

  useEffect(() => {
    const exactAddress = formData.exactAddress || "";
    setQuery(exactAddress);
    setSelectedCity(exactAddress);
  }, [formData.exactAddress]);

  return (
    <div ref={wrapperRef} className="relative col-span-4 w-full">
      <Input
        id="exactAddress"
        name="exactAddress"
        value={query}
        onChange={handleChange}
        autoComplete="off"
        placeholder={copy.cityPlaceholder}
        className="col-span-3"
      />

      {showDropdown && (
        <ul className="absolute left-0 right-0 z-[60] mt-1 max-h-48 overflow-y-auto rounded-lg border border-border/70 bg-background text-foreground shadow-xl">
          {filtered.map((city) => (
            <li
              key={city}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(city);
              }}
              className="cursor-pointer px-3 py-2 text-sm text-foreground hover:bg-hover"
            >
              {city}
            </li>
          ))}

          {noMatch && (
            <li className="cursor-default px-3 py-2 text-sm italic text-foreground">
              {copy.cityNotFound}
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
